// Durable submit queue for pilot slips. A submission that can't reach the
// server RIGHT NOW (dead ship wifi, offline hotel, PPU) is stored on the
// device and sent automatically when the network returns — the pilot taps
// Submit once and is done. Entries live in localStorage under submitQueue_*
// (mirrored to the IndexedDB vault by draftVault, so they survive iOS
// storage wipes) and are drained by AuthContext on reconnect/boot.
//
// Only NETWORK failures queue. A server rejection (bad payload, auth denied)
// is a real error the pilot must see — queueing it would hide a problem that
// retrying can never fix.

import { supabase } from "../supabaseClient";
import { releaseSlipCopy } from "./localDraftSpace";
import { applyServerCorrections, applyServerBaseTimes } from "../../supabase/functions/_shared/slipSegments.js";

const QUEUE_PREFIX = "submitQueue_";
const SUBMIT_TIMEOUT_MS = 90000;

// Ship wifi HANGS instead of failing — every submit network step gets a
// deadline so the button can't spin for minutes (audit 2026-07-11).
export function withTimeout(promise, ms = SUBMIT_TIMEOUT_MS, label = "The server didn't answer in time") {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(label)), ms)),
  ]);
}

export function isNetworkError(err) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return /failed to fetch|load failed|network|didn't answer|timeout|abort|internet/i.test(err?.message || "");
}

const notifyChanged = () => {
  try { window.dispatchEvent(new CustomEvent("submit-queue-changed")); } catch { /* ignore */ }
};

export function listQueued() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(QUEUE_PREFIX)) continue;
      try { out.push({ key, ...JSON.parse(localStorage.getItem(key)) }); } catch { /* corrupt entry */ }
    }
  } catch { /* storage unavailable */ }
  return out.sort((a, b) => (a.queuedAt || 0) - (b.queuedAt || 0));
}

export function pendingSubmitCount() {
  return listQueued().length;
}

export function isSlipQueued(tripId) {
  try { return localStorage.getItem(`${QUEUE_PREFIX}slip_${tripId}`) != null; } catch { return false; }
}

export function enqueueSlipSubmit(tripId, payload) {
  try {
    localStorage.setItem(`${QUEUE_PREFIX}slip_${tripId}`, JSON.stringify({
      kind: "slip",
      tripId: String(tripId),
      payload,
      queuedAt: Date.now(),
      attempts: 0,
    }));
    notifyChanged();
    return true;
  } catch {
    return false;
  }
}

// A handoff to another pilot queues exactly like a submission when the
// network is dark. entry: { trip_id, to_auth_user_id | to_pilot_info_id,
// to_name, from_name, vessel_name, voyage_number, draft }.
export function enqueueHandoff(entry) {
  try {
    localStorage.setItem(
      `${QUEUE_PREFIX}handoff_${entry.trip_id}_${entry.to_auth_user_id || entry.to_pilot_info_id}`,
      JSON.stringify({ kind: "handoff", entry, queuedAt: Date.now(), attempts: 0 })
    );
    notifyChanged();
    return true;
  } catch {
    return false;
  }
}

async function sendQueuedHandoff(queued) {
  const e = { ...queued.entry };
  if (!e.to_auth_user_id && e.to_pilot_info_id) {
    const { data: rec, error } = await withTimeout(
      supabase.from("pilots_info").select("auth_user_id").eq("id", e.to_pilot_info_id).single(), 15000
    );
    if (error) throw new Error(error.message);
    if (!rec?.auth_user_id) throw new Error("That pilot doesn't have a portal login yet.");
    e.to_auth_user_id = rec.auth_user_id;
  }
  const { to_pilot_info_id: _drop, to_name: _drop2, ...row } = e;
  const { error } = await withTimeout(supabase.from("slip_handoffs").insert(row));
  if (error) throw new Error(error.message);
  return true;
}

// Everything that must happen AFTER the server accepts a slip: DB status +
// totals (Fleet Slip History reads these), segment rows, local draft flipped
// to submitted, cloud draft mirror deleted, submitted index updated, pending
// flag cleared. Shared by the editor's direct path and the queue drainer so
// the two can never diverge.
// result: the server's reply. When its office-table gate corrected a leg
// (miles, base time, or a blank where the table has no time), the reply
// carries the corrections, and everything saved here (segment rows, the filed
// envelope, total miles) uses them, so the database says what the PDF and the
// JSON say. A reply from a server deployed before 2026-10-11 carries only
// base times; those still apply.
export const SLIP_FILED_EVENT = "seapa:slip-filed";
export async function finalizeSlipSubmit(tripId, sentPayload, result = null) {
  // First, before any await: an editor still open on this slip (a queued
  // submit drained while it sat on screen) stops saving now, so nothing it
  // writes can land over the filed copy (Codex on #518).
  try { window.dispatchEvent(new CustomEvent(SLIP_FILED_EVENT, { detail: { tripId: String(tripId) } })); } catch { /* no window */ }
  // ...and in every other tab of this browser (Codex on #518).
  try { const ch = new BroadcastChannel(SLIP_FILED_EVENT); ch.postMessage({ tripId: String(tripId) }); ch.close(); } catch { /* no BroadcastChannel */ }
  // Slow steaming is the office's to work out (decision 1, 2026-10-11): the
  // saved copy carries none, whatever an older server or phone sent.
  const corrected = result?.corrections?.length
    ? applyServerCorrections(sentPayload.segments, result.corrections)
    : result?.base_times?.length
      ? applyServerBaseTimes(sentPayload.segments, result.base_times)
      : sentPayload.segments;
  const payload = {
    ...sentPayload,
    segments: (corrected || []).map((s) => ({ ...s, actual_time_hours: null, slow_steaming_hours: null })),
    total_slow_steaming_hours: null,
  };
  const submittedTotalMiles = (payload.segments || []).reduce(
    (a, s) => a + (Number(s.distance_nm) || 0) + (Number(s.extra_miles) || 0), 0);
  try {
    await supabase
      .from("pilot_trips_v2")
      .update({
        status: "submitted",
        submitted_at: new Date().toISOString(),
        total_miles: submittedTotalMiles || null,
        notes: payload.notes ?? null,
      })
      .eq("id", tripId);
  } catch (e) { console.warn("Submitted-status sync failed (JSON/PDF still authoritative):", e); }
  try {
    const segRows = (payload.segments || []).map((s, i) => ({
      trip_id: tripId,
      segment_number: i + 1,
      location_name: s.location_name || null,
      time_in: s.time_in || null,
      time_out: s.time_out || null,
      distance_nm: s.distance_nm === "" || s.distance_nm == null ? null : Number(s.distance_nm),
      extra_miles: s.extra_miles === "" || s.extra_miles == null ? null : Number(s.extra_miles),
      base_time_hours: s.base_time_hours === "" || s.base_time_hours == null ? null : Number(s.base_time_hours),
      slow_steaming_hours: null,
      notes: s.note || null,
    }));
    if (segRows.length) {
      await supabase.from("trip_segments").delete().eq("trip_id", tripId);
      const { error: segErr } = await supabase.from("trip_segments").insert(segRows);
      if (segErr) console.warn("Submitted-segment sync failed (JSON/PDF still authoritative):", segErr);
    }
  } catch (e) { console.warn("Submitted-segment sync skipped:", e); }

  // Closing the loop (Jill 2026-07-12): if this slip arrived by handoff,
  // tell the sender's Paperwork page who submitted it and when.
  try {
    const handoffRowId = localStorage.getItem(`handoffRow_${tripId}`);
    if (handoffRowId) {
      let byName = null;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const { data: me } = await supabase.from("pilots_info").select("name_short, full_name").eq("auth_user_id", session?.user?.id).maybeSingle();
        byName = me?.name_short || me?.full_name || session?.user?.email || null;
      } catch { /* name is best-effort */ }
      await supabase.from("slip_handoffs")
        .update({ submitted_at: new Date().toISOString(), submitted_by_name: byName })
        .eq("id", handoffRowId);
      localStorage.removeItem(`handoffRow_${tripId}`);
    }
  } catch (e) { console.warn("Handoff submitted-notice skipped:", e); }

  try { localStorage.removeItem(`pilotSlipPending_${tripId}`); } catch { /* ignore */ }
  // The slip is filed, so it is no longer unsaved work sitting on this device.
  // Without this the marker set by the editor's save-as-you-type (2026-07-25)
  // outlives the submission and computeSyncSummary nags "1 item waiting to
  // sync" forever, which is exactly the kind of false alarm that teaches
  // pilots to ignore the one warning that matters.
  try { localStorage.removeItem(`pilotSlipUnsaved_${tripId}`); } catch { /* ignore */ }
  try {
    const submittedPayload = { ...payload, status: "submitted" };
    const timestamp = Date.now();
    // machine:true — this write is bookkeeping, not a pilot edit. The
    // workspace un-archives a trip when its draft is edited AFTER archiving;
    // a queued submit draining hours later must not resurrect an archived
    // trip to the top of the picker (Jill's ROY, 7/31).
    const envelope = { timestamp, data: submittedPayload, version: (submittedPayload.version ?? 1) + 1, machine: true };
    localStorage.setItem(`pilotSlipDraft_${tripId}`, JSON.stringify(envelope));
    // Filed → the account, then OFF the device (Jill's ruling 2026-08-12:
    // "finished paperwork needs a connection, full stop"). The final envelope
    // (full fidelity: signatures, dates, notes) is written locally above FIRST,
    // then upserted to slip_drafts under slip_state='filed'. Only when the
    // server CONFIRMS that upsert does the device copy go — never on this
    // device's own belief that it filed (the July RLS incident: a client can
    // think a write happened that silently didn't). If the upsert fails, the
    // local final envelope stays put and the seedCloudDrafts reconcile sweep
    // uploads-then-releases it on the next online open, so a dead-wifi filing
    // can never strand a fat envelope on the phone forever the way the
    // 2026-08 stuck-'draft' rows did.
    //
    // FIRE-AND-FORGET, exactly like before (review 2026-07-25): this block
    // must never sit between the server accepting the slip and the local
    // truth writes below — an awaited network step there delays the success
    // alert on boat wifi and widens the window where a retry could
    // double-submit (the dedupe reads the submittedIndex written below). The
    // slip_draft_history trigger keeps the pre-submit generation recoverable.
    (async () => {
      try {
        const { data: { session } } = await withTimeout(supabase.auth.getSession(), 10000);
        const uid = session?.user?.id;
        if (!uid) return;
        const { error: fileErr } = await withTimeout(
          supabase.from("slip_drafts").upsert({
            auth_user_id: uid,
            trip_id: String(tripId),
            draft: envelope,
            saved_at: new Date(timestamp).toISOString(),
            vessel_name: submittedPayload.vessel?.name || submittedPayload.vessel_name || null,
            voyage_number: submittedPayload.voyage_number || null,
            slip_state: "filed",
            filed_at: new Date(timestamp).toISOString(),
          }, { onConflict: "auth_user_id,trip_id" }),
          10000
        );
        if (fileErr) {
          console.warn("Filed-envelope upsert rejected — device copy KEPT; the reconcile sweep will retry on the next online open:", fileErr);
          return;
        }
        // The account now provably holds the filed envelope of record.
        releaseSlipCopy(tripId, "filing confirmed by the account");
      } catch (e) { console.warn("Filed-envelope preservation skipped (offline?) — device copy kept for the reconcile sweep:", e); }
    })();
    const submittedIndex = JSON.parse(localStorage.getItem("pilotSlipSubmittedIndex") || "[]");
    const filtered = submittedIndex.filter((s) => s.id !== tripId);
    filtered.push({
      id: tripId,
      voyage_number: submittedPayload.voyage_number,
      vessel: submittedPayload.vessel,
      status: "submitted",
      updated_at: timestamp,
    });
    localStorage.setItem("pilotSlipSubmittedIndex", JSON.stringify(filtered));
  } catch (err) {
    console.warn("Could not overwrite local draft/submitted index:", err);
  }
}

async function sendQueuedSlip(entry) {
  // Already submitted since it was queued (e.g. the pilot reopened the slip
  // online and submitted directly)? Drop the entry — never double-email.
  try {
    const idx = JSON.parse(localStorage.getItem("pilotSlipSubmittedIndex") || "[]");
    if (idx.some((s) => s.id === entry.tripId && (s.updated_at || 0) >= (entry.queuedAt || 0))) return true;
  } catch { /* index unreadable — proceed */ }
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token || "";
  if (!accessToken) return false; // wait for a real session — retried on next drain
  const { data: result, error } = await withTimeout(
    supabase.functions.invoke("submit_pilot_slip", {
      body: entry.payload,
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  );
  if (error || !result?.ok) throw new Error(result?.error || error?.message || "Submit failed");
  await finalizeSlipSubmit(entry.tripId, entry.payload, result);
  return true;
}

let draining = false;
export async function drainSubmitQueue() {
  if (draining) return;
  const entries = listQueued();
  if (!entries.length) return;
  draining = true;
  let sentAny = false;
  try {
    for (const entry of entries) {
      if (entry.kind !== "slip" && entry.kind !== "handoff") continue;
      try {
        const done = entry.kind === "handoff"
          ? await sendQueuedHandoff(entry)
          : await sendQueuedSlip(entry);
        if (done) {
          localStorage.removeItem(entry.key);
          sentAny = true;
        }
      } catch (e) {
        if (isNetworkError(e)) break; // still dark — try the whole queue later
        // Server rejected it: stop retrying silently, keep the entry but mark
        // it so the UI can tell the pilot to reopen and resubmit.
        try {
          const raw = JSON.parse(localStorage.getItem(entry.key));
          raw.attempts = (raw.attempts || 0) + 1;
          raw.lastError = e?.message || "rejected";
          raw.lastTriedAt = Date.now();
          localStorage.setItem(entry.key, JSON.stringify(raw));
        } catch { /* ignore */ }
      }
    }
  } finally {
    draining = false;
    if (sentAny) notifyChanged();
  }
}

// The browser's own reconnect signal (not trusted alone for UI, but a fine
// trigger to ATTEMPT a drain — a failed attempt just stays queued).
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { drainSubmitQueue(); });
}
